import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { getInstrumentStateChip } from '../utils';
import type { RunInstrument } from '../hooks/useRunInstruments';
import type { CellStatus } from '@features/executionDashboard/hooks/useCellStatuses';

type Props = {
  instrument: RunInstrument;
  isSelected: boolean;
  onSelect: (woDetailGlobalId: string) => void;
  liveStatus?: CellStatus['status'] | null;
  isActive?: boolean;
  activeProgress?: number | null;
  showRerun?: boolean;
  rerunDisabled?: boolean;
  onRerun?: () => void;
};

const liveChipColor = (
  status: CellStatus['status']
): 'success' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'running':
      return 'info';
    default:
      return 'default';
  }
};

const RailRow: React.FC<Props> = ({
  instrument,
  isSelected,
  onSelect,
  liveStatus = null,
  isActive = false,
  activeProgress = null,
  showRerun = false,
  rerunDisabled = false,
  onRerun
}) => {
  const { t } = useTranslation('calibration');

  const handleClick = useCallback(() => {
    onSelect(instrument.woDetailGlobalId);
  }, [onSelect, instrument.woDetailGlobalId]);

  const handleRerunClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onRerun?.();
    },
    [onRerun]
  );

  const historyChip = getInstrumentStateChip(instrument, t);
  const liveChip = liveStatus
    ? {
        label: t(`runPage.liveState.${liveStatus}`, {
          defaultValue: liveStatus
        }),
        color: liveChipColor(liveStatus)
      }
    : null;
  const chip = liveChip ?? historyChip;

  return (
    <Box
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        borderLeft: theme =>
          `3px solid ${
            isActive
              ? theme.palette.info.main
              : isSelected
                ? theme.palette.primary.main
                : 'transparent'
          }`,
        backgroundColor: theme =>
          isSelected || isActive
            ? theme.palette.action.selected
            : 'transparent',
        '&:hover': {
          backgroundColor: theme => theme.palette.action.hover
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1
        }}
      >
        <Chip
          label={instrument.cell ?? '—'}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace', minWidth: 52 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {instrument.equipmentId ||
              t('runPage.unknownEquipment', { defaultValue: 'Unknown' })}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {instrument.description ?? instrument.companyName ?? ''}
          </Typography>
        </Box>
        <Chip
          label={chip.label}
          size="small"
          color={chip.color}
          variant="outlined"
        />
        {showRerun && (
          <Tooltip
            title={
              rerunDisabled
                ? t('runPage.rerunDisabledTip', {
                    defaultValue: 'No rack assignment for this instrument'
                  })
                : t('runPage.rerunTip', {
                    defaultValue: 'Re-run this instrument'
                  })
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={rerunDisabled}
                onClick={handleRerunClick}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      {isActive && (
        <LinearProgress
          variant={activeProgress != null ? 'determinate' : 'indeterminate'}
          value={activeProgress ?? undefined}
          color="info"
          sx={{ height: 3 }}
        />
      )}
    </Box>
  );
};

export default RailRow;
